import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ApiCreateDocumentGenerationJob, ApiService, CompanyDocuments } from 'api';
import { documentsUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { FormDialog } from 'components';
import { useDocuments, useResponse } from 'services/hooks';
import { POLL_TYPE, useAppLoadAsyncOperations } from 'services/hooks/useAppLoadAsyncActions';
import DocumentGenerationJobsCreateModalProps from './DocumentGenerationJobCreateModal.props';
import { CREATE_MODAL_CONSTANTS, QUERY_CONSTANTS } from '../../constants';
import { AIGradientButton } from '../AIGradient';

const DocumentGenerationJobCreateModal = ({ ...rest }: DocumentGenerationJobsCreateModalProps) => {
  const queryClient = useQueryClient();
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();

  const [preCreatedJob, setPreCreatedJob] = useState<ApiCreateDocumentGenerationJob>({
    reference_files: [],
    processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
    desired_termination_state: 'COMPLETE',
    parser_overrides: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { errorNotification } = useResponse();
  const { manuallyExecutePoll } = useAppLoadAsyncOperations(false);

  const { apiCompanyDocumentGenerationJobsNewCreate } = ApiService;
  const {
    getDocuments,
    companyDocuments,
    isLoading: isLoadingDocuments,
  } = useDocuments() as unknown as {
    getDocuments: (companyId: number, measurementDataId: number) => Promise<void>;
    companyDocuments: CompanyDocuments[];
    isLoading: boolean;
  };

  // any as use store is not typed
  const [{ companyInfo }] = useStore() as any;

  const memoizedGetDocuments = useMemo(() => getDocuments, [getDocuments]);

  const configureJobByType = useCallback((): ApiCreateDocumentGenerationJob => {
    switch (preCreatedJob.processing_type) {
      case ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE:
        return {
          ...preCreatedJob,
          parser_overrides: [
            {
              file_type: 'xlsx',
              xlsx_parsing_mode: 'JSON_CONVERSION',
              minify_json: true,
            },
          ],
        };
      default:
        return preCreatedJob;
    }
  }, [preCreatedJob]);

  const handleCreate = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    rest.onClose?.({}, 'backdropClick');
    try {
      const jobToCreate = configureJobByType();
      await apiCompanyDocumentGenerationJobsNewCreate(companyInfo?.id, jobToCreate as any);

      queryClient.invalidateQueries({
        queryKey: [QUERY_CONSTANTS.getDocumentGenerationJobs, companyInfo.id, 0],
        exact: false,
      });

      // Start polling so the backend syncs job statuses with the external service.
      // The polling loop stops automatically once no active jobs remain.
      manuallyExecutePoll(POLL_TYPE.GENERATION_JOBS);

      // any as the exception is not typed
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error;
      errorNotification(errorMessage || CREATE_MODAL_CONSTANTS.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    rest,
    configureJobByType,
    apiCompanyDocumentGenerationJobsNewCreate,
    companyInfo.id,
    queryClient,
    manuallyExecutePoll,
    errorNotification,
  ]);

  useEffect(() => {
    const getData = async () => {
      await memoizedGetDocuments(companyInfo.id, companyInfo.measurement_data_id);
    };

    getData();
    // need this disable getDocuments has a rerendering issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  return (
    <FormDialog
      onClose={() => rest.onClose && rest.onClose({}, 'backdropClick')}
      open={rest.open}
      title={CREATE_MODAL_CONSTANTS.TITLE}
      onSave={handleCreate}
      isValid={preCreatedJob.reference_files.length > 0 && !!preCreatedJob.processing_type}
      showSaveButton={false}
      customButton={
        <AIGradientButton
          onClick={handleCreate}
          disabled={isSubmitting || !(preCreatedJob.reference_files.length > 0 && !!preCreatedJob.processing_type)}>
          {CREATE_MODAL_CONSTANTS.ACTION_BUTTON}
        </AIGradientButton>
      }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div>
          <Typography variant="h3">{CREATE_MODAL_CONSTANTS.DOCUMENT_ACTION}</Typography>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isLoadingDocuments || companyDocuments === undefined ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : companyDocuments.length > 0 ? (
            <Autocomplete
              multiple
              id="checkboxes-documents"
              options={companyDocuments.flatMap(doc => doc.files || [])}
              disableCloseOnSelect
              onChange={(_, v) =>
                setPreCreatedJob({
                  ...preCreatedJob,
                  reference_files: v?.map(file => file?.file.id?.toString()) as any,
                })
              }
              getOptionLabel={option => option?.file?.filename ?? 'Unknown file'}
              getOptionKey={option => option.document_reference_id}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option?.file
                      ? `${option.file.file_path}/${option.file.filename}.${option.file.file_type}`
                      : 'Unknown file'}
                  </li>
                );
              }}
              style={{ width: '100%' }}
              renderInput={params => (
                <TextField
                  variant="outlined"
                  {...params}
                  placeholder={CREATE_MODAL_CONSTANTS.DOCUMENT_SELECT_PLACEHOLDER}
                />
              )}
              isOptionEqualToValue={(option, value) => option.document_reference_id === value.document_reference_id}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No documents available to process.
              </Typography>
              <Button
                component={Link}
                to={documentsUrl(firmSlugParam ?? '', companySlugParam ?? '')}
                variant="outlined"
                color="primary"
                onClick={() => rest.onClose?.({}, 'backdropClick')}>
                Go to Documents
              </Button>
            </Box>
          )}
        </div>
        <div>
          <Typography variant="h3">{CREATE_MODAL_CONSTANTS.TYPE_ACTION}</Typography>
          <Select
            value={preCreatedJob.processing_type}
            onChange={e =>
              setPreCreatedJob(prev => ({
                ...prev,
                processing_type: e.target.value as ApiCreateDocumentGenerationJob.processing_type,
              }))
            }
            variant="outlined"
            style={{ width: '100%' }}>
            {CREATE_MODAL_CONSTANTS.OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    </FormDialog>
  );
};

export default DocumentGenerationJobCreateModal;
